import Api from '../axios/api';
import { OrderInterface } from '../components/OrdersTable/interfaces/Order.interface';
import { TruckDto, TruckTelematicsDto } from './truck.services';
import { Contract } from './contracts.service';

export interface PublicOrderDto extends OrderInterface {
	loadedQuantity?: number;
	trucks?: TruckDto[];
	contract?: Contract;
}

export interface PublicOrderResponse {
	data: PublicOrderDto;
}

export interface OrderShareTokenDto {
	token: string;
	orderId: number;
	extensionId: number;
	createdAt: string;
	expiresAt: string;
}

export interface OrderShareTokenResponse {
	data: OrderShareTokenDto;
}

/** The last fix the backend holds for a truck, with the driver app state. */
export interface LastKnownLocationDto {
	lat: number;
	long: number;
	/** capture time of that fix (ISO) */
	recordedAt?: string;
	speedKmh?: number;
	address?: string;
	/** last check-in of the driver app (ISO), moving or not */
	lastSeenAt?: string;
	isOnline?: boolean;
}

export interface OrderTruckTrackingDto {
	id: number;
	telematics?: TruckTelematicsDto;
	lastLocation?: LastKnownLocationDto;
}

export interface OrderTrackingResponse {
	data: { trucks: OrderTruckTrackingDto[] };
}

export interface FleetLocationPointDto {
	lat: number;
	long: number;
	/** recorded time, epoch ms */
	timestamp: number;
	/** km/h at this fix */
	speed?: number;
}

export interface FleetLocationHistoryDto {
	items?: FleetLocationPointDto[];
	count?: number;
	lastLocation?: LastKnownLocationDto;
}

export interface FleetLocationHistoryResponse {
	data: FleetLocationHistoryDto;
}

export interface FleetHistoryQuery {
	/** window start, epoch ms */
	from?: number;
	/** window end, epoch ms */
	to?: number;
	limit?: number;
}

/** Roles whose order page can read the trucks' GPS history. */
export const TRACKING_ROLES = ['admin', 'transporter', 'producer', 'customer'];
/** Roles that may read a truck's breadcrumb trail outside the order. */
export const FLEET_HISTORY_ROLES = ['admin', 'transporter'];
export const FLEET_HISTORY_MAX_POINTS = 5000;

export const TRACKING_SHARE_EXPIRES_IN_DAYS = 30;

export class TrackingService {
	static createShareToken(
		orderId: number,
		expiresInDays: number = TRACKING_SHARE_EXPIRES_IN_DAYS,
	): Promise<OrderShareTokenResponse> {
		return Api.post(`/v1/admin/orders/${orderId}/share`, { expiresInDays });
	}

	static getOrderTracking(
		target: string,
		orderId: number,
	): Promise<OrderTrackingResponse> {
		return Api.get(`/v1/${target}/orders/${orderId}/tracking`);
	}

	/** The breadcrumb trail of one truck (fleet id) and its last fix. */
	static getFleetHistory(
		target: string,
		fleetId: number,
		query: FleetHistoryQuery = {},
	): Promise<FleetLocationHistoryResponse> {
		return Api.get(`/v1/${target}/fleets/${fleetId}/locations/history`, {
			params: { limit: FLEET_HISTORY_MAX_POINTS, ...query },
		});
	}

	static getPublicOrder(token: string): Promise<PublicOrderResponse> {
		return Api.get(`/v1/public/orders/${encodeURIComponent(token)}`);
	}
}
